import { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";
import { FormControl, Card, Grid, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import MDBox from "@vendor/components/MDBox";
import MDButton from "@vendor/components/MDButton";
import { BasicLayout } from "@shared/ui/auth/BasicLayout";
import bgImage from "@assets/images/sign_in_image_background.jpg";
import Coderland from "@assets/images/logos/logo.png";
import Loading from "@vendor/examples/loading";
import SnackbarComponent from "@vendor/components/Snackbar";
import { setLayout, useMaterialUIController } from "@vendor/context";
import ChangeLanguageComponent from "@shared/ui/i18n/ChangeLanguageComponent";

import { useSignIn } from "../hooks/useSignIn";

/** Campos que el formulario de login registra. */
interface SignInFormValues {
  username: string;
  password: string;
}

/** Forma del error de axios que la pantalla lee para mostrar el mensaje del backend. */
interface ApiError {
  response?: { data?: { message?: string } };
}

export const SignInPage = () => {
  const { t } = useTranslation();
  localStorage.setItem("token", "");
  const { signIn, isSubmitting } = useSignIn();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [message, setMessage] = useState("");
  const [, dispatch] = useMaterialUIController();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SignInFormValues>({ mode: "onChange" });

  useEffect(() => {
    setLayout(dispatch, "pages");
  }, []);

  const handleClose = (_event?: unknown, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  const onSignIn = async (value: SignInFormValues) => {
    try {
      await signIn(value);
    } catch (error) {
      setMessage((error as ApiError).response?.data?.message || t("internalError"));
      setOpenSnackbar(true);
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <SnackbarComponent
        open={openSnackbar}
        handleClose={handleClose}
        message={message}
        alert="error"
      />
      <Loading open={isSubmitting} />
      <Card>
        <MDBox
          bgColor="black"
          borderRadius="lg"
          coloredShadow="grey"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <Grid container spacing={2} sx={{ justifyContent: "center", mt: 1, mb: 2 }}>
            <img alt="Logo Coderland" src={Coderland} width="90%" height="90%" />
          </Grid>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <form onSubmit={handleSubmit(onSignIn)}>
            <MDBox mb={2}>
              <Controller
                name="username"
                control={control}
                defaultValue=""
                rules={{ required: true }}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <TextField
                      label={t("usernameField")}
                      margin="dense"
                      fullWidth
                      {...field}
                      type="text"
                      name="username"
                      variant="outlined"
                    />
                  </FormControl>
                )}
              />
              {errors.username && errors.username.type === "required" && (
                <span style={{ fontSize: 12, color: "red" }}>{t("isRequired")}</span>
              )}
            </MDBox>
            <MDBox mb={2}>
              <Controller
                name="password"
                control={control}
                defaultValue=""
                rules={{ required: true }}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <TextField
                      label={t("passwordField")}
                      margin="dense"
                      type="password"
                      fullWidth
                      {...field}
                      name="password"
                      variant="outlined"
                    />
                  </FormControl>
                )}
              />
              {errors.password && errors.password.type === "required" && (
                <span style={{ fontSize: 12, color: "red" }}>{t("isRequired")}</span>
              )}
            </MDBox>
            <MDBox mb={2}>
              <ChangeLanguageComponent />
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton variant="gradient" type="submit" color="info" fullWidth>
                {t("loginButton")}
              </MDButton>
            </MDBox>
          </form>
        </MDBox>
      </Card>
    </BasicLayout>
  );
};
